import React, { useState, useCallback, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  Badge,
  Box,
  Grid,
  Container,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  MenuItem,
  Collapse,
  IconButton,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Delete';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { format, startOfMonth, endOfMonth, subMonths, format as formatDate } from 'date-fns';
import axiosApi from '../Utils/axiosApi';
import HeaderMenu from '../Header/HeaderMenu';
import AbsenceDialog from '../HostelDetails/AbsenceDialog';
import HostelerInvoice from '../hostelerPayment/HostelerInvoice';
import CreateHostelerNotification from '../Notification/CreateHostelerNotification';

const HostellerDashboard = () => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [billingMode, setBillingMode] = useState(null); // PRE_BILLING | POST_BILLING

  const [hostelerData, setHostelerData] = useState(null);

  // Today's Meal
  const [todayMenu, setTodayMenu] = useState(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [mealError, setMealError] = useState(null);

  // Notifications
  //const [notifications, setNotifications] = useState([]);
  const [receivedNotifications, setReceivedNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [openReceived, setOpenReceived] = useState(true);
  const [openSent, setOpenSent] = useState(true);

  // Absence Dialog
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [absenceFormData, setAbsenceFormData] = useState({});
// Hostel Policy
const [hostelPolicy, setHostelPolicy] = useState('');


  // Invoice & Payment
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [billPaid, setBillPaid] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paidAmount: 0,
    balanceAmount: 0,
    amountToPay: 0,
    mode: 'Cash',
    remarks: '',
    transactionDescription: '',
  });

  const [alertMessage, setAlertMessage] = useState(null);
  const [statusMessage] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [open, setOpen] = useState(false); // Create/Edit Notification Dialog
  const [unavailabilityDays, setUnavailabilityDays] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Today's Add-On Items
	const [todayAddOns, setTodayAddOns] = useState([]);



  // Helper: Month start & end
  const getMonthRange = (monthString) => {
    const date = new Date(`${monthString}-01`);
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return {
      start: formatDate(start, 'yyyy-MM-dd'),
      end: formatDate(end, 'yyyy-MM-dd'),
    };
  };

  // Create Notification Button
  const handleCreate = () => {
    setSelectedNotification(null);
    setIsUpdate(false);
    setOpen(true);
  };

const formatPolicyByHeading = (text) => {
  if (!text || typeof text !== 'string') return [];

  // Matches headings like:
  // General Rule
  // Fees Rule:
  // MESS RULE -
  // Hostel Rule
  const headingRegex = /([A-Z][A-Za-z\s]*Rule)\s*[:-]?\s*/g;


  const matches = [...text.matchAll(headingRegex)];

  // 🔹 Fallback: no headings detected
  if (matches.length === 0) {
    return [
      {
        title: 'Hostel Policy',
        description: text.trim(),
      },
    ];
  }

  const sections = [];

  matches.forEach((match, index) => {
    const title = match[1].trim();
    const startIndex = match.index + match[0].length;
    const endIndex = matches[index + 1]?.index || text.length;

    const description = text
      .substring(startIndex, endIndex)
      .replace(/\s+/g, ' ')
      .trim();

    if (description) {
      sections.push({
        title,
        description,
      });
    }
  });

  return sections;
};





  const handleClose = () => {
    setOpen(false);
    setSelectedNotification(null);
    setIsUpdate(false);
  };

  // Fetch Today's Meal
  const fetchTodaysMeal = useCallback(async (hostelId) => {
  if (!hostelId) {
    setTodayMenu(null);
    return;
  }
  try {
    setMealLoading(true);
    setMealError(null);

    const today = new Date();
    const localCurrentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    const response = await axiosApi.post('/dashBoard/getTodayMealPlan', { hostelIds: [hostelId], currentDate: localCurrentDate });

    if (response?.data) {
      if (response.data.hostelClosure) {
        // Hostel is closed
        setTodayMenu({ hostelClosure: true });
        setTodayAddOns([]);
      } else {
        setTodayMenu(response.data);
        setTodayAddOns(response.data.hostelAddOnMenuItemDTOs || []);
      }
    } else {
      setTodayMenu(null);
      setTodayAddOns([]);
    }
  } catch (err) {
    console.error('Error fetching today\'s meal:', err);
    setMealError('Failed to load today\'s menu.');
    setTodayMenu(null);
    setTodayAddOns([]);
  } finally {
    setMealLoading(false);
  }
}, []);

const fetchHostelBillingMode = useCallback(async (hostelId) => {
  if (!hostelId) return;

  try {
    const response = await axiosApi.post(
      '/hostelerBillingConfiguration/getHostelBillingMode',
      { hostelIds: [hostelId] }
    );

    // Backend returns plain string
    setBillingMode(response.data || 'PRE_BILLING');
  } catch (error) {
    console.error('Failed to fetch billing mode', error);
    setBillingMode('PRE_BILLING'); // fallback
  }
}, []);

  // Fetch Notifications
  const fetchNotifications = useCallback(async (hostelerId, hostelId) => {
    try {
      setLoadingNotifications(true);
      setNotificationError(null);

      const response = await axiosApi.post(
        '/hostelNotification/getHostelerDashBoardNotification',
        { hostelerId, hostelId }
      );

      const data = Array.isArray(response.data) ? response.data : [];

      // ✅ Categorize based on directionType directly from API
     const received = data.filter(
  (n) =>
    n.directionType === 'RECEIVED' ||
    (n.directionType == null && n.source === 'SYSTEM')
);
      const sent = data.filter((n) => n.directionType === 'SENT');

      //setNotifications(data);
      setReceivedNotifications(received);
      setSentNotifications(sent);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotificationError('Failed to load notifications.');
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

const fetchHostelPolicy = useCallback(async (hostelId) => {
  if (!hostelId) return;

  try {
    const response = await axiosApi.post('/dashBoard/getHostelPolicy', {
      hostelIds: [hostelId],
    });

    const data = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    setHostelPolicy(data?.hostelRuleDescription || '');
  } catch (err) {
    console.error('Error fetching hostel policy:', err);
    setHostelPolicy('');
  }
}, []);

  // Fetch Hosteler Data
  const fetchHostlerData = useCallback(async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      if (!userId) return;
      const response = await axiosApi.post('/hosteler/getHosteler', { userIds: [userId] });
      if (Array.isArray(response.data) && response.data.length > 0) {
        const hosteler = response.data[0];
        setHostelerData(hosteler);
        fetchNotifications(hosteler.id, hosteler.hostelId);
        // fetch today's meal once we know hostelId
        fetchTodaysMeal(hosteler.hostelId);
		fetchHostelPolicy(hosteler.hostelId); 
		fetchHostelBillingMode(hosteler.hostelId);
      }
    } catch (err) {
      console.error('Error fetching hosteler:', err);
      setNotificationError('Failed to fetch hosteler details.');
    }
  }, [fetchNotifications, fetchTodaysMeal,fetchHostelPolicy,fetchHostelBillingMode]);

  useEffect(() => {
    fetchHostlerData();
  }, [fetchHostlerData]);


useEffect(() => {
  if (!billingMode) return;

  const month =
    billingMode === 'POST_BILLING'
      ? format(subMonths(new Date(), 1), 'yyyy-MM') // previous month
      : format(new Date(), 'yyyy-MM');               // current month

  setSelectedMonth(month);
}, [billingMode]);

  // Fetch Invoice
  const fetchInvoice = useCallback(async (hostelerId, billMonth) => {
    try {
      const response = await axiosApi.post('/hostelerPayment/getHostelerInvoice', {
        hostelerIds: [hostelerId],
        billingMonth: billMonth,
      });

      // 🧩 Handle array or object safely
      const data = Array.isArray(response.data) ? response.data[0] : response.data;

      if (data) {
        setSelectedInvoice(data);
        setBillPaid(data.paymentStatus === 'COMPLETED');

        setPaymentForm((f) => ({
          ...f,
          amount: data.pendingAmount ?? data.invoiceTotalAmount ?? '',
        }));
      } else {
        setSelectedInvoice(null);
        setBillPaid(false);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setAlertMessage({ type: 'error', message: 'Failed to fetch invoice details.' });
    }
  }, []);

  // Fetch Unavailability Days
  const fetchUnavailabilityDays = useCallback(async (hostelerId, hostelId, monthString) => {
    try {
      const { start, end } = getMonthRange(monthString);
      const response = await axiosApi.post('/hostelerLeaveRequest/getHostelLeaveRequest', {
        hostelerIds: [hostelerId],
        hostelIds: [hostelId],
        leaveStartDate: start,
        leaveEndDate: end,
      });
      if (Array.isArray(response.data)) {
        let totalDays = 0;
        response.data.forEach((absence) => {
          if (absence.leaveStartDate && absence.leaveEndDate) {
            const startDate = new Date(absence.leaveStartDate);
            const endDate = new Date(absence.leaveEndDate);
            const diffTime = endDate.getTime() - startDate.getTime();
            totalDays += Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
          }
        });
        setUnavailabilityDays(totalDays);
      } else {
        setUnavailabilityDays(0);
      }
    } catch (err) {
      console.error('Error fetching unavailability days:', err);
      setUnavailabilityDays(0);
    }
  }, []);

  useEffect(() => {
    if (hostelerData && selectedMonth) {
      fetchInvoice(hostelerData.id, selectedMonth);
      fetchUnavailabilityDays(hostelerData.id, hostelerData.hostelId, selectedMonth);
    }
  }, [hostelerData, selectedMonth, fetchInvoice, fetchUnavailabilityDays]);

  // Handle Payment
  const handlePaymentSubmit = async () => {
    const payNow = Number(paymentForm.amountToPay) || 0;
    const alreadyPaid = Number(paymentForm.paidAmount) || 0;
    const total = Number(paymentForm.amount) || 0;
    const newPaid = alreadyPaid + payNow;

    if (payNow <= 0) {
      setAlertMessage({ type: 'error', message: 'Please enter an amount to pay.' });
      return;
    }

    try {
      await axiosApi.post('/hostelerPayment/updateHostelerPayment', {
        hostelerIds: [hostelerData.id],
        hostelId: hostelerData.hostelId,
        billingMonth: selectedMonth,
        paymentAmount: payNow, // ✅ only current payment
        paymentMode: paymentForm.mode,
        paymentRemarks: paymentForm.remarks,
        paymentTransactionDescription: paymentForm.transactionDescription,
      });

      setBillPaid(newPaid >= total);
      setAlertMessage({ type: 'success', message: 'Payment updated successfully ✅' });
      fetchInvoice(hostelerData.id, selectedMonth);
      setPaymentDialogOpen(false);
    } catch (err) {
      console.error('Payment update error:', err);
      setAlertMessage({ type: 'error', message: 'Failed to update payment.' });
    }
  };

  const handlePayBill = async () => {
    if (!hostelerData) return;

    try {
      const response = await axiosApi.post('/hostelerPayment/getHostelerInvoice', {
        hostelerIds: [hostelerData.id],
        billingMonth: selectedMonth,
      });

      const data = Array.isArray(response.data) ? response.data[0] : response.data;

      if (data) {
        // ✅ Clear any previous alert
        setAlertMessage(null);
        const totalAmount = data.invoiceTotalAmount || 0;
        const paidAmount = data.paidAmount || 0;

        setPaymentForm({
          amount: totalAmount,
          paidAmount: paidAmount,
          balanceAmount: totalAmount - paidAmount,
          amountToPay: 0,
          mode: 'Cash',
          remarks: '',
          transactionDescription: '',
        });

        // ✅ Open payment dialog
        setSelectedInvoice(data);
        setPaymentDialogOpen(true);
        setInvoiceOpen(false);
      } else {
        setAlertMessage({
          type: 'warning',
          message: `No bill found for ${format(new Date(`${selectedMonth}-01`), 'MMMM yyyy')}.`,
        });
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setAlertMessage({ type: 'error', message: 'Failed to fetch invoice.' });
    }
  };

  const handleViewBill = async () => {
    if (!hostelerData) return;

    try {
      const response = await axiosApi.post('/hostelerPayment/getHostelerInvoice', {
        hostelerIds: [hostelerData.id],
        billingMonth: selectedMonth,
      });

      const data = Array.isArray(response.data) ? response.data[0] : response.data;

      if (data) {
        // ✅ Open invoice view dialog instead of payment
        // ✅ Clear any previous alert
        setAlertMessage(null);
        setSelectedInvoice(data);
        setInvoiceOpen(true);
        setPaymentDialogOpen(false);
      } else {
        // ✅ Show banner message if no bill found
        setAlertMessage({
          type: 'warning',
          message: `No bill found for ${format(new Date(`${selectedMonth}-01`), 'MMMM yyyy')}.`,
        });
        setInvoiceOpen(false);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setAlertMessage({ type: 'error', message: 'Failed to fetch invoice.' });
    }
  };

  // Delete Sent Notification
  const handleDeleteNotification = async (notificationId) => {
    try {
      await axiosApi.post('/hostelNotification/deleteHostelNotification', {
        hostelNotificationIds: [notificationId],
      });
      setSentNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setAlertMessage({ type: 'success', message: 'Notification deleted successfully.' });
    } catch (err) {
      console.error('Error deleting notification:', err);
      setAlertMessage({ type: 'error', message: 'Failed to delete notification.' });
    } finally {
      setConfirmDelete(null);
    }
  };
  const billingMonthDate = new Date(`${selectedMonth}-01`);
 const displayMonth =
    billingMode === 'PRE_BILLING'
      ? format(subMonths(billingMonthDate, 1), 'MMMM yyyy')
      : format(billingMonthDate, 'MMMM yyyy');
	  
	  
  return (
    <div>
      <HeaderMenu />
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Header */}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="h5">
              {hostelerData
                ? `${hostelerData.hostelerName}'s Dashboard – ${hostelerData.hostelName}`
                : 'Hosteller Dashboard'}
            </Typography>
            <Badge badgeContent={receivedNotifications.length} color="error">
              <NotificationsIcon fontSize="large" />
            </Badge>
          </Stack>

         <Box
  sx={{
    mt: 3,
    p: { xs: 2, sm: 3 },
    borderRadius: 4,
    backdropFilter: "blur(12px)",
    background: "rgba(255,255,255,0.6)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    border: "1px solid rgba(255,255,255,0.4)",
    transition: "0.3s ease",
  }}
>
  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
    <Typography
      variant="h5"
      fontWeight="bold"
      sx={{
        background: "linear-gradient(45deg, #FF6B6B, #5F27CD)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        fontSize: { xs: "18px", sm: "22px" },
      }}
    >
      🍽️ Today's Meal Plan
    </Typography>
  </Stack>

  {/* Loading */}
  {mealLoading && (
    <Stack alignItems="center" py={2}>
      <CircularProgress size={24} />
      <Typography variant="body2" color="text.secondary" mt={1}>
        Fetching today's menu...
      </Typography>
    </Stack>
  )}

  {/* Error */}
  {mealError && (
    <Alert severity="error" sx={{ mt: 2 }}>
      {mealError}
    </Alert>
  )}

  {/* Hostel Closed */}
  {todayMenu?.hostelClosure ? (
    <Alert severity="info" sx={{ mt: 2 }}>
      🏠 The hostel is closed today. No meals are available.
    </Alert>
  ) : (
    todayMenu && (
      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        {[
          {
            title: "Breakfast",
            icon: "🌞",
            menu: todayMenu.todayBreakfastMenu,
            time: todayMenu.todayBreakfastTime,
            color: "#FF9F43",
          },
          {
            title: "Lunch",
            icon: "🍛",
            menu: todayMenu.todayLunchMenu,
            time: todayMenu.todayLunchTime,
            color: "#1DD1A1",
          },
          {
            title: "Snacks",
            icon: "☕",
            menu: todayMenu.todaySnacksMenu,
            time: todayMenu.todaySnacksTime,
            color: "#54a0ff",
          },
          {
            title: "Dinner",
            icon: "🌙",
            menu: todayMenu.todayDinnerMenu,
            time: todayMenu.todayDinnerTime,
            color: "#5f27cd",
          },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 4,
                backdropFilter: "blur(14px)",
                background: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                transition: "all 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                },
              }}
            >
              <Typography fontWeight={700} color={item.color}>
                {item.icon} {item.title}
              </Typography>

              <Typography mt={1}>
                {item.menu || "-"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                ⏱ {item.time || "-"}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    )
  )}
</Box>

{/* ================= Add-On Items ================= */}
{!todayMenu?.hostelClosure && todayAddOns.length > 0 ? (
  <Box mt={4}>
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        background:
          "linear-gradient(135deg, rgba(255,245,235,0.95), rgba(255,255,255,0.95))",
        border: "1px dashed rgba(255,167,38,0.5)",
      }}
    >
      <Stack spacing={1.5}>
        <Typography fontWeight={700} color="#e17055">
          🧾 Today’s Add-On Items
        </Typography>

        <Divider />

        {todayAddOns.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>➕ {item.itemName}</Typography>
            <Chip label={`₹${item.itemPrice}`} color="success" />
          </Box>
        ))}
      </Stack>
    </Paper>
  </Box>
) : (
  !todayMenu?.hostelClosure && (
    <Typography
      variant="caption"
      sx={{
        mt: 2,
        display: "block",
        textAlign: "center",
        color: "text.secondary",
        fontStyle: "italic",
      }}
    >
      No add-on items available today
    </Typography>
  )
)}





<Paper
  sx={{
    p: 3,
    mt: 3,
    borderRadius: 3,
    background:
      'linear-gradient(135deg, rgba(245,247,255,0.95), rgba(255,255,255,0.95))',
  }}
>
  <Typography variant="h6" fontWeight={700} mb={2}>
    📜 Hostel Policy
  </Typography>

  {(!hostelPolicy || formatPolicyByHeading(hostelPolicy).length === 0) ? (
    <Typography variant="body2" color="text.secondary">
      No policy available.
    </Typography>
  ) : (
    formatPolicyByHeading(hostelPolicy).map((section, index) => (
      <Accordion
        key={index}
        defaultExpanded={index === 0}
        sx={{
          mb: 1,
          borderRadius: 2,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700} color="primary">
            {section.title}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.7, color: 'text.secondary' }}
          >
            {section.description}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))
  )}
</Paper>








          {/* show any alert messages */}
          {alertMessage && (
            <Alert
              severity={alertMessage.type}
              onClose={() => setAlertMessage(null)}
              sx={{ mt: 2 }}
            >
              {alertMessage.message}
            </Alert>
          )}

          {/* Billing Section */}
          <Box mt={4}>
            <TextField
              type="month"
              label="Select Month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">
                Hostel Bill – {format(new Date(`${selectedMonth}-01`), 'MMMM yyyy')}
              </Typography>
              <Chip label={billPaid ? 'Paid ✅' : 'Unpaid ❌'} color={billPaid ? 'success' : 'warning'} />
            </Stack>
            {selectedInvoice && (
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Typography>💰 Total Bill: ₹{selectedInvoice.invoiceTotalAmount}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>🍽 Meal Charges: ₹{selectedInvoice.mealFinalAmount}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>🏠 Rent Charges: ₹{selectedInvoice.rentFinalAmount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>❌ Unavailability Days: {unavailabilityDays}{' '}
        <span style={{ color: '#666' }}>({displayMonth})</span></Typography>
                </Grid>
              </Grid>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              {!billPaid && (
                <Button variant="contained" onClick={handlePayBill} fullWidth sx={{ maxWidth: 200 }}>
                  Pay Bill
                </Button>
              )}
              <Button variant="outlined" color="secondary" onClick={handleViewBill} fullWidth sx={{ maxWidth: 200 }}>
                View Bill
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Absences + Create Notification Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="flex-start"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            {/* Manage Absences Button */}
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{
                maxWidth: { sm: 250 },
                textTransform: 'none',
                fontWeight: 600,
              }}
              onClick={async () => {
                if (!hostelerData) return;
                try {
                  const response = await axiosApi.post('/hostelerLeaveRequest/getHostelLeaveRequest', {
                    hostelerIds: [hostelerData.id],
                    hostelIds: [hostelerData.hostelId],
                  });
                  setAbsenceFormData({
                    hostelerId: hostelerData.id,
                    hostelerName: hostelerData.hostelerName,
                    hostelId: hostelerData.hostelId,
                    hostelName: hostelerData.hostelName,
                    absences: Array.isArray(response.data) ? response.data : [],
                  });
                  setAbsenceDialogOpen(true);
                } catch (err) {
                  console.error('Error fetching leave requests:', err);
                  setAlertMessage({ type: 'error', message: 'Failed to fetch leave requests.' });
                }
              }}
              disabled={!hostelerData}
            >
              Manage Absences
            </Button>

            {/* Create Suggestions/Complaints Button (same style) */}
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{
                maxWidth: { sm: 290 },
                textTransform: 'none',
                fontWeight: 600,
              }}
              onClick={handleCreate}
            >
              Create Suggestions/Complaints
            </Button>
          </Stack>

          {/* Notifications */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6">Notifications</Typography>
          {loadingNotifications && <CircularProgress size={24} sx={{ mt: 1 }} />}
          {notificationError && <Alert severity="error">{notificationError}</Alert>}

          {/* Received Notifications */}
          {!loadingNotifications && receivedNotifications.length > 0 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={2}
                sx={{
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  📥 Incoming Messages ({receivedNotifications.length})
                </Typography>
                <IconButton
                  onClick={() => setOpenReceived((prev) => !prev)}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                >
                  {openReceived ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={openReceived}>
                <List dense>
                  {receivedNotifications.map((note) => (
                    <ListItem key={note.id}>
                      <ListItemText
                        primary={`Title: ${note.title}`}
                        secondary={`Message: ${note.message}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Sent Notifications */}
          {!loadingNotifications && sentNotifications.length > 0 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={3}
                sx={{
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  📤 Sent Items({sentNotifications.length})
                </Typography>
                <IconButton
                  onClick={() => setOpenSent((prev) => !prev)}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  {openSent ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Collapse in={openSent}>
                <List dense>
                  {sentNotifications
                    .filter((note) => note.directionType === 'SENT')
                    .map((note) => (
                      <ListItem
                        key={note.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <ListItemText
                          primary={note.title}
                          secondary={
                            <>
                              {note.message && <Typography variant="body2">Message: {note.message}</Typography>}
                              {note.feedbackStatus && <Typography variant="body2">Feedback Status: {note.feedbackStatus}</Typography>}
                              {note.reply && <Typography variant="body2">Reply: {note.reply}</Typography>}
                            </>
                          }
                          sx={{ maxWidth: '85%' }}
                        />
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setConfirmDelete(note.id)}
                          disabled={!(note.directionType === 'SENT' && !note.reply)}
                          sx={{
                            width: 28,
                            height: 28,
                            padding: 0,
                            '&:hover': {
                              backgroundColor: note.directionType === 'SENT' && !note.reply
                                ? 'rgba(255,0,0,0.1)'
                                : 'transparent',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </>
          )}

          {/* No Notifications */}
          {!loadingNotifications &&
            receivedNotifications.length === 0 &&
            sentNotifications.length === 0 &&
            !notificationError && <Typography>No new notifications 🎉</Typography>}

          {statusMessage && <Alert severity="info" sx={{ mt: 2 }}>{statusMessage}</Alert>}
        </Paper>
      </Container>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this notification?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            onClick={() => handleDeleteNotification(confirmDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      <AbsenceDialog
        open={absenceDialogOpen}
        onClose={() => setAbsenceDialogOpen(false)}
        absences={absenceFormData?.absences || []}
        hostelId={absenceFormData?.hostelId}
        hostelName={absenceFormData?.hostelName}
        hostelerId={absenceFormData?.hostelerId}
        hostelerName={absenceFormData?.hostelerName}
      />

      <HostelerInvoice open={invoiceOpen} onClose={() => setInvoiceOpen(false)} hosteler={selectedInvoice} />

      {/* Hostel Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hostel Payment</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {/* 🔹 Hosteler & Billing Month - Read Only */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hosteler Name"
                  fullWidth
                  value={hostelerData?.hostelerName || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Billing Month"
                  fullWidth
                  value={selectedMonth || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            {/* Total Amount - Read Only */}
            <TextField
              label="Total Amount"
              fullWidth
              value={paymentForm.amount}
              InputProps={{ readOnly: true }}
            />

            {/* Paid Amount - Read Only */}
            <TextField
              label="Total Paid Amount"
              type="number"
              fullWidth
              value={paymentForm.paidAmount || ''}
              InputProps={{ readOnly: true }}
            />

            {/* Balance Amount - Read Only */}
            <TextField
              label="Remaining Balance"
              type="number"
              fullWidth
              value={paymentForm.balanceAmount || ''}
              InputProps={{ readOnly: true }}
            />

            {/* Going to Pay Amount */}
            <TextField
              label="Amount to Pay"
              type="number"
              fullWidth
              value={paymentForm.amountToPay || ''}
              onChange={(e) => {
                const total = Number(paymentForm.amount) || 0;
                const alreadyPaid = Number(paymentForm.paidAmount) || 0;
                let payNow = Number(e.target.value) || 0;

                // Prevent overpayment
                if (payNow + alreadyPaid > total) payNow = total - alreadyPaid;

                setPaymentForm({
                  ...paymentForm,
                  amountToPay: payNow,
                  balanceAmount: total - (alreadyPaid + payNow),
                });
              }}
              inputProps={{ min: 0, max: Number(paymentForm.amount) }}
            />

            {/* Payment Mode */}
            <TextField
              select
              label="Payment Mode"
              fullWidth
              value={paymentForm.mode}
              onChange={(e) => setPaymentForm({ ...paymentForm, mode: e.target.value })}
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
            </TextField>

            {/* Payment Remarks */}
            <TextField
              label="Payment Remarks"
              fullWidth
              multiline
              minRows={2}
              value={paymentForm.remarks}
              onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
            />

            {/* Transaction Description (only if Online) */}
            {paymentForm.mode === 'Online' && (
              <TextField
                label="Transaction Description"
                fullWidth
                multiline
                minRows={2}
                value={paymentForm.transactionDescription}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, transactionDescription: e.target.value })
                }
              />
            )}

            {/* Alert */}
            {alertMessage && <Alert severity={alertMessage.type}>{alertMessage.message}</Alert>}

            <Button variant="contained" onClick={handlePaymentSubmit}>
              Update Payment
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Notification Dialog */}
      <Dialog
        open={open} // your state
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose(); // only allow close via button or escape key
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{isUpdate ? 'Edit Suggestions/Complaints' : 'Create Suggestions/Complaints'}</DialogTitle>
        <DialogContent>
          <CreateHostelerNotification
            isDialog
            isUpdate={isUpdate}
            initialData={selectedNotification}
            recipientHostelerId={hostelerData?.id}
            recipientHostelId={hostelerData?.hostelId}
            onSuccess={() => {
              fetchNotifications(hostelerData.id, hostelerData.hostelId);
              handleClose();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HostellerDashboard;
